import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import BracketMatchCard from "./BracketMatchCard";
import BracketTree from "./BracketTree";
import type { BracketDisplayMode } from "./navigation";
import {
  accentColor,
  isDarkMode,
  RESULTATS_TRANSITION,
} from "./resultatsTheme";
import type { PlayerDirectory, Round } from "./types";
import styles from "./Resultats.module.css";

interface BracketByRoundViewProps {
  rounds: Round[];
  players: PlayerDirectory;
  bracketLinkMode?: string | null;
  selectedTourId: string | null;
  mode: BracketDisplayMode;
  highlightedLicence?: string | null;
  onSelectLicence?: (licence: string) => void;
  onSelectTour: (tourId: string) => void;
  onSelectMode: (mode: BracketDisplayMode) => void;
}

export default function BracketByRoundView({
  rounds,
  players,
  bracketLinkMode = null,
  selectedTourId,
  mode,
  highlightedLicence = null,
  onSelectLicence,
  onSelectTour,
  onSelectMode,
}: BracketByRoundViewProps): React.ReactElement {
  const theme = useTheme();
  const isDark = isDarkMode(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const connected = bracketLinkMode === "ROUND_ORDER";
  const effectiveMode: BracketDisplayMode =
    mode === "tree" && connected ? "tree" : "rounds";
  const accent = accentColor(theme);

  const orderedRounds = useMemo(
    () =>
      [...rounds].sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) || a.label.localeCompare(b.label)
      ),
    [rounds]
  );

  const activeRound = useMemo(() => {
    if (selectedTourId) {
      const found = orderedRounds.find((r) => r.id === selectedTourId);
      if (found) return found;
    }
    return orderedRounds[0] ?? null;
  }, [orderedRounds, selectedTourId]);

  if (orderedRounds.length === 0) {
    return (
      <Alert severity="info">Aucune rencontre éliminatoire disponible.</Alert>
    );
  }

  return (
    <Stack spacing={2.25}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.25,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          value={effectiveMode}
          onChange={(_event, value: BracketDisplayMode | null) => {
            if (value) onSelectMode(value);
          }}
          aria-label="Mode d'affichage du tableau"
          sx={{
            bgcolor: alpha(
              isDark ? theme.palette.common.white : theme.palette.primary.main,
              isDark ? 0.06 : 0.06
            ),
            borderRadius: 2,
            p: 0.35,
            "& .MuiToggleButton-root": {
              border: 0,
              borderRadius: 1.5,
              px: 1.25,
              py: 0.6,
              textTransform: "none",
              fontWeight: 700,
              color: "text.secondary",
              "&.Mui-selected": {
                bgcolor: theme.palette.background.paper,
                color: accent,
                boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.18)}`,
              },
            },
          }}
        >
          <ToggleButton value="rounds" aria-label="Par tour">
            <ViewAgendaOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
            Par tour
          </ToggleButton>
          <ToggleButton
            value="tree"
            aria-label="Arbre complet"
            disabled={!connected}
          >
            <AccountTreeOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
            Arbre
          </ToggleButton>
        </ToggleButtonGroup>

        {effectiveMode === "tree" && isMobile && (
          <Typography variant="caption" color="text.secondary">
            Sur mobile, le mode par tour reste le plus lisible.
          </Typography>
        )}
      </Box>

      {!connected && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Liens entre tours non disponibles — affichage par tour uniquement.
        </Alert>
      )}

      {effectiveMode === "rounds" && (
        <>
          <Box
            className={styles.roundSelector}
            role="tablist"
            aria-label="Tours"
          >
            {orderedRounds.map((round) => {
              const selected = activeRound?.id === round.id;
              return (
                <Button
                  key={round.id}
                  role="tab"
                  aria-selected={selected}
                  size="small"
                  disableElevation
                  onClick={() => onSelectTour(round.id)}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 999,
                    px: 1.5,
                    transition: RESULTATS_TRANSITION,
                    bgcolor: selected
                      ? accent
                      : alpha(
                          isDark
                            ? theme.palette.common.white
                            : theme.palette.primary.main,
                          isDark ? 0.08 : 0.07
                        ),
                    color: selected
                      ? isDark
                        ? theme.palette.grey[900]
                        : theme.palette.primary.contrastText
                      : "text.primary",
                    "&:hover": {
                      bgcolor: selected
                        ? accent
                        : alpha(
                            isDark
                              ? theme.palette.common.white
                              : theme.palette.primary.main,
                            isDark ? 0.14 : 0.12
                          ),
                    },
                  }}
                >
                  {round.label}
                  <Box
                    component="span"
                    sx={{
                      ml: 0.7,
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      opacity: selected ? 0.85 : 0.7,
                    }}
                  >
                    {round.matches.length}
                  </Box>
                </Button>
              );
            })}
          </Box>

          {activeRound && (
            <Box className={styles.roundMatchesList}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                  mb: 0.25,
                }}
              >
                {activeRound.label}
              </Typography>
              {activeRound.matches.length === 0 ? (
                <Typography color="text.secondary">
                  Aucun match dans ce tour.
                </Typography>
              ) : (
                activeRound.matches.map((game) => (
                  <BracketMatchCard
                    key={game.id}
                    game={game}
                    players={players}
                    highlightedLicence={highlightedLicence}
                    onSelectLicence={onSelectLicence}
                    pathHighlighted={
                      Boolean(highlightedLicence) &&
                      game.players.includes(highlightedLicence)
                    }
                  />
                ))
              )}
            </Box>
          )}
        </>
      )}

      {effectiveMode === "tree" && (
        <BracketTree
          rounds={orderedRounds}
          players={players}
          bracketLinkMode={bracketLinkMode}
          highlightedLicence={highlightedLicence}
          onSelectLicence={onSelectLicence}
        />
      )}
    </Stack>
  );
}
